import React from 'react';

interface PasswordRequirement {
  label: string;
  test: (password: string) => boolean;
}

const requirements: PasswordRequirement[] = [
  {
    label: 'At least 8 characters',
    test: (password) => password.length >= 8,
  },
  {
    label: 'At least one uppercase letter',
    test: (password) => /[A-Z]/.test(password),
  },
  {
    label: 'At least one lowercase letter',
    test: (password) => /[a-z]/.test(password),
  },
  {
    label: 'At least one number',
    test: (password) => /[0-9]/.test(password),
  },
  {
    label: 'At least one special character',
    test: (password) => /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password),
  },
];

interface PasswordRequirementsProps {
  password: string;
  show: boolean;
}

export const PasswordRequirements: React.FC<PasswordRequirementsProps> = ({ password, show }) => {
  if (!show) return null;
  const getRequirementStatus = (test: (password: string) => boolean) => {
    if (!password) return 'pending';
    return test(password) ? 'met' : 'unmet';
  };

  return (
    <div className="absolute z-50 w-full mt-1">
      {/* Arrow pointing up */}
      <div className="flex justify-center mb-[-1px]">
        <div
          className="w-0 h-0 border-l-8 border-r-8 border-b-8"
          style={{
            borderLeftColor: 'transparent',
            borderRightColor: 'transparent',
            borderBottomColor: '#2D2D2D',
          }}
        />
      </div>
      {/* Tooltip content */}
      <div
        className="p-3 rounded-lg shadow-2xl"
        style={{
          backgroundColor: '#2D2D2D',
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.3)',
        }}
      >
        <p className="text-xs font-semibold mb-2.5 text-white">
          Password Requirements:
        </p>
        <ul className="space-y-1.5">
          {requirements.map((requirement, index) => {
            const status = getRequirementStatus(requirement.test);
            return (
              <li key={index} className="flex items-center text-xs">
                <span className="mr-2.5 flex-shrink-0">
                  {status === 'met' ? (
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      style={{ color: '#10B981' }}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  ) : status === 'unmet' ? (
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      style={{ color: '#EF4444' }}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  ) : (
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      style={{ color: '#9CA3AF' }}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  )}
                </span>
                <span
                  style={{
                    color:
                      status === 'met'
                        ? '#10B981'
                        : status === 'unmet'
                        ? '#EF4444'
                        : '#E5E7EB',
                  }}
                >
                  {requirement.label}
                </span>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};

